import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '@/components/Header';
import { Mail, Trash2, CheckCircle, AlertCircle } from 'lucide-react';

function DeleteAccountPage() {
  const { slug } = useParams();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    reason: '',
    dataOptions: {
      personalData: false,
      activityHistory: false,
      accountInfo: false
    }
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAppData();
  }, [slug]);

  const fetchAppData = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/apps/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setApp(data);
      }
    } catch (err) {
      console.error('Error fetching app:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (option) => {
    setFormData(prev => ({
      ...prev,
      dataOptions: {
        ...prev.dataOptions,
        [option]: !prev.dataOptions[option]
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.email || !formData.reason) {
      setError('Please fill in all required fields');
      return;
    }

    const hasSelectedData = Object.values(formData.dataOptions).some(val => val);
    if (!hasSelectedData) {
      setError('Please select at least one data deletion option');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/delete-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          appSlug: slug,
          appName: app?.name || '',
          ...formData
        })
      });

      if (response.ok) {
        setSubmitted(true);
        // Reset form
        setFormData({
          email: '',
          reason: '',
          dataOptions: {
            personalData: false,
            activityHistory: false,
            accountInfo: false
          }
        });
      } else {
        setError('Failed to submit request. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again or contact support directly.');
    }
  };

  const generateMailtoLink = () => {
    const subject = `Account Deletion Request - ${app?.name || 'App'}`;
    const body = `Hello Perfect Peace Enterprise Support Team,

I would like to request the deletion of my account for ${app?.name || 'the app'}.

My Email: ${formData.email || '[Your Email]'}

Reason for Deletion:
${formData.reason || '[Please explain your reason]'}

Data to Delete:
${formData.dataOptions.personalData ? '✓ All Personal Data\n' : ''}${formData.dataOptions.activityHistory ? '✓ Activity History / Catch Logs\n' : ''}${formData.dataOptions.accountInfo ? '✓ Account Information\n' : ''}

I understand this request will be processed within 60 days.

Thank you.`;

    return `mailto:Support@perfectpeaceenterprise.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-white text-lg">Loading...</div>
        </div>
      </div>
    );
  }

  if (!app) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-white text-lg">App not found</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      <Header />
      
      <main className="container mx-auto px-4 py-16 max-w-3xl">
        {/* Header */}
        <div className="text-center mb-12">
          {app.logo && (
            <img 
              src={app.logo} 
              alt={app.name} 
              className="w-24 h-24 mx-auto mb-6 rounded-2xl"
            />
          )}
          <h1 className="text-4xl font-bold text-white mb-4">
            Delete Your Account
          </h1>
          <p className="text-lg text-slate-300">
            {app.name}
          </p>
        </div>

        {/* Success Message */}
        {submitted && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6 mb-8 flex items-start gap-4">
            <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-lg font-semibold text-green-400 mb-2">
                Request Submitted Successfully
              </h3>
              <p className="text-slate-300">
                Your account deletion request has been received. Our team will process your request within 60 days. 
                You will receive a confirmation email at the address you provided.
              </p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 mb-8 flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-lg font-semibold text-red-400 mb-2">Error</h3>
              <p className="text-slate-300">{error}</p>
            </div>
          </div>
        )}

        {/* Information Card */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-white mb-4">
            Before You Delete Your Account
          </h2>
          <ul className="space-y-3 text-slate-300">
            <li className="flex items-start gap-3">
              <span className="text-blue-400 mt-1">•</span>
              <span>Account deletion is <strong className="text-white">permanent</strong> and cannot be undone.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-blue-400 mt-1">•</span>
              <span>All selected data will be permanently removed from our systems.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-blue-400 mt-1">•</span>
              <span>Your request will be processed within <strong className="text-white">60 days</strong>.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-blue-400 mt-1">•</span>
              <span>You will receive a confirmation email once the deletion is complete.</span>
            </li>
          </ul>
        </div>

        {/* Form */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-white mb-6">
            Submit Deletion Request
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                Your Email Address <span className="text-red-400">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="your.email@example.com"
              />
            </div>

            {/* Reason Field */}
            <div>
              <label htmlFor="reason" className="block text-sm font-medium text-slate-300 mb-2">
                Reason for Deletion <span className="text-red-400">*</span>
              </label>
              <textarea
                id="reason"
                name="reason"
                value={formData.reason}
                onChange={handleInputChange}
                required
                rows={4}
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Please briefly explain why you're deleting your account..."
              />
            </div>

            {/* Data Options */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-3">
                What data should we delete? <span className="text-red-400">*</span>
              </label>
              <div className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={formData.dataOptions.personalData}
                    onChange={() => handleCheckboxChange('personalData')}
                    className="mt-1 w-5 h-5 rounded border-slate-700 bg-slate-900/50 text-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0"
                  />
                  <div>
                    <span className="text-white font-medium group-hover:text-blue-400 transition-colors">
                      All Personal Data
                    </span>
                    <p className="text-sm text-slate-400 mt-1">
                      Name, email, profile information, and settings
                    </p>
                  </div>
                </label>

                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={formData.dataOptions.activityHistory}
                    onChange={() => handleCheckboxChange('activityHistory')}
                    className="mt-1 w-5 h-5 rounded border-slate-700 bg-slate-900/50 text-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0"
                  />
                  <div>
                    <span className="text-white font-medium group-hover:text-blue-400 transition-colors">
                      Activity History / Catch Logs
                    </span>
                    <p className="text-sm text-slate-400 mt-1">
                      All logged activities, catches, trips, and usage history
                    </p>
                  </div>
                </label>

                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={formData.dataOptions.accountInfo}
                    onChange={() => handleCheckboxChange('accountInfo')}
                    className="mt-1 w-5 h-5 rounded border-slate-700 bg-slate-900/50 text-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0"
                  />
                  <div>
                    <span className="text-white font-medium group-hover:text-blue-400 transition-colors">
                      Account Information
                    </span>
                    <p className="text-sm text-slate-400 mt-1">
                      Account credentials and authentication data
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* Processing Time Notice */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
              <p className="text-sm text-blue-300">
                ⏱️ <strong>Processing Time:</strong> Your request will be processed within 60 days. 
                You'll receive a confirmation email once completed.
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 group"
            >
              <Trash2 className="w-5 h-5 group-hover:scale-110 transition-transform" />
              Submit Deletion Request
            </button>
          </form>
        </div>

        {/* Email Direct Contact */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-8">
          <h2 className="text-xl font-bold text-white mb-4">
            Prefer to Email Directly?
          </h2>
          <p className="text-slate-300 mb-6">
            You can also send your deletion request directly to our support team via email.
          </p>
          <a
            href={generateMailtoLink()}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 group"
          >
            <Mail className="w-5 h-5 group-hover:scale-110 transition-transform" />
            Email Support Team
          </a>
          <p className="text-sm text-slate-400 mt-4 text-center">
            Support@perfectpeaceenterprise.com
          </p>
        </div>

        {/* Back Link */}
        <div className="text-center mt-8">
          <Link 
            to={`/apps/${slug}`}
            className="text-blue-400 hover:text-blue-300 transition-colors"
          >
            ← Back to {app.name}
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/[0.06] bg-slate-950">
        <div className="mx-auto max-w-7xl px-6 py-10 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_c83bb9d1-4481-4216-be0b-e2d2e530a3a4/artifacts/j5gcpl1b_Peaceful%20harmony%20in%20a%20logo%20design.png"
              alt="Perfect Peace Enterprise Logo"
              className="h-10 w-auto object-contain"
            />
            <div>
              <div className="text-base font-semibold text-white">
                Perfect Peace Enterprise
              </div>
              <p className="mt-0.5 text-xs text-slate-500">
                Building digital products for real-world impact.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-6 text-sm text-slate-500">
            <Link to="/" className="hover:text-white transition-colors">
              Home
            </Link>
            <Link to="/privacy" className="hover:text-white transition-colors">
              Privacy
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default DeleteAccountPage;
